/**
 * Post Media Components
 * 
 * Components for displaying media content in posts:
 * - PostImageGallery: Image carousel with fullscreen view
 * - PollDisplay: Interactive poll with voting
 */

import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, BarChart3, Check } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { API_URL } from '../utils/constants';
import { useDesign } from '@/contexts/DesignContext';

/**
 * Image Gallery with Carousel
 * 
 * Features:
 * - Single image: Full width display
 * - Multiple images: Carousel with navigation arrows and dots
 * - Click to view fullscreen
 * 
 * @param {Object} props
 * @param {string[]} props.images - Array of image URLs
 */
export function PostImageGallery({ images }) {
  const { isStudio } = useDesign();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  
  if (!images || images.length === 0) return null;
  
  // Single image - show full width
  if (images.length === 1) {
    return (
      <>
        <div 
          className={isStudio ? 'st-gal mb-3 cursor-pointer' : "mb-3 cursor-pointer"}
          onClick={() => setFullscreenImage(images[0])}
        >
          <img 
            src={images[0]} 
            alt="Post image" 
            className="rounded-lg max-w-full max-h-96 object-cover"
          />
        </div>
        {fullscreenImage && (
          <Dialog open={!!fullscreenImage} onOpenChange={() => setFullscreenImage(null)}>
            <DialogContent className="max-w-4xl p-0 bg-transparent border-0">
              <img 
                src={fullscreenImage} 
                alt="Full size" 
                className="max-w-full max-h-[90vh] object-contain mx-auto"
              />
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }
  
  // Multiple images - show carousel
  const nextImage = () => setCurrentIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  
  return (
    <>
      <div className={isStudio ? 'st-gal mb-3 relative' : "mb-3 relative"}>
        <div 
          className="relative overflow-hidden rounded-lg cursor-pointer"
          onClick={() => setFullscreenImage(images[currentIndex])}
        >
          <img 
            src={images[currentIndex]} 
            alt={`Post image ${currentIndex + 1}`} 
            className="w-full max-h-96 object-cover"
          />
          {/* Image counter */}
          <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
            {currentIndex + 1} / {images.length}
          </div>
        </div>
        
        {/* Navigation arrows */}
        <button
          onClick={(e) => { e.stopPropagation(); prevImage(); }}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 dark:bg-[#2d2f33]/90 hover:bg-white dark:hover:bg-white/10 rounded-full flex items-center justify-center shadow-md"
        >
          <ChevronLeft className="w-5 h-5 text-slate-700 dark:text-slate-200" />
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); nextImage(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/90 dark:bg-[#2d2f33]/90 hover:bg-white dark:hover:bg-white/10 rounded-full flex items-center justify-center shadow-md"
        >
          <ChevronRight className="w-5 h-5 text-slate-700 dark:text-slate-200" />
        </button>
        
        {/* Dots indicator */}
        <div className="flex justify-center gap-1.5 mt-2">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={isStudio
                ? `st-dot${idx === currentIndex ? ' st-on' : ''}`
                : `w-2 h-2 rounded-full transition-colors ${
                idx === currentIndex ? 'bg-blue-500' : 'bg-slate-300 hover:bg-slate-400'
              }`}
            />
          ))}
        </div>
      </div>
      
      {/* Fullscreen modal */}
      {fullscreenImage && (
        <Dialog open={!!fullscreenImage} onOpenChange={() => setFullscreenImage(null)}>
          <DialogContent className="max-w-4xl p-0 bg-transparent border-0">
            <div className="relative">
              <img 
                src={fullscreenImage} 
                alt="Full size" 
                className="max-w-full max-h-[90vh] object-contain mx-auto"
              />
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => {
                      const newIndex = (images.indexOf(fullscreenImage) - 1 + images.length) % images.length;
                      setFullscreenImage(images[newIndex]);
                      setCurrentIndex(newIndex);
                    }}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 dark:bg-[#2d2f33]/90 hover:bg-white dark:hover:bg-white/10 rounded-full flex items-center justify-center shadow-md"
                  >
                    <ChevronLeft className="w-6 h-6 text-slate-700 dark:text-slate-200" />
                  </button>
                  <button
                    onClick={() => {
                      const newIndex = (images.indexOf(fullscreenImage) + 1) % images.length;
                      setFullscreenImage(images[newIndex]);
                      setCurrentIndex(newIndex);
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 dark:bg-[#2d2f33]/90 hover:bg-white dark:hover:bg-white/10 rounded-full flex items-center justify-center shadow-md"
                  >
                    <ChevronRight className="w-6 h-6 text-slate-700 dark:text-slate-200" />
                  </button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

/**
 * Interactive Poll Display
 * 
 * Features:
 * - Vote on options
 * - Progress bars after voting
 * - "Other" option support
 * - Optimistic UI updates
 * 
 * @param {Object} props
 * @param {Object} props.post - Post object containing poll data
 * @param {Object} props.user - Current user object
 * @param {Function} props.onUpdate - Callback when poll is updated
 */
export function PollView({ poll: pollProp, currentUserId, voterNames = {}, onVote, onVoteOther, readOnly = false, hideQuestion = false }) {
  const { isStudio } = useDesign();
  const [otherResponse, setOtherResponse] = useState('');
  const [submittingVote, setSubmittingVote] = useState(false);
  const [localPoll, setLocalPoll] = useState(() => JSON.parse(JSON.stringify(pollProp)));
  const [forceUpdate, setForceUpdate] = useState(0);

  // Resync when the authoritative poll changes (feed refetch OR DM onSnapshot).
  useEffect(() => {
    setLocalPoll(JSON.parse(JSON.stringify(pollProp)));
  }, [pollProp]);

  const poll = localPoll;
  if (!poll) return null;
  // 113 frame 1 writes the closing day into the poll's meta line; the field is
  // already on the poll the API returns, classic simply never showed it.
  const closesLabel = (() => {
    if (!poll.closes_at) return '';
    const d = new Date(poll.closes_at);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { weekday: 'long' });
  })();
  const allowMultiple = !!poll.allow_multiple;

  // Handle both old format (poll.votes) and new format (option.votes)
  const getOptionVotes = (optIndex) => {
    const option = poll.options[optIndex];
    if (typeof option === 'object' && Array.isArray(option.votes)) {
      return option.votes;
    }
    if (poll.votes && poll.votes[optIndex]) {
      return poll.votes[optIndex];
    }
    if (poll.votes && poll.votes[String(optIndex)]) {
      return poll.votes[String(optIndex)];
    }
    return [];
  };

  // Calculate total votes
  const totalVotes = poll.options.reduce((sum, _, idx) => sum + getOptionVotes(idx).length, 0) + (poll.other_responses?.length || 0);

  // Check if user has voted
  const userVoteIndex = poll.options.findIndex((_, idx) => getOptionVotes(idx).includes(currentUserId));
  const userOtherVote = poll.other_responses?.find(r => r.user_id === currentUserId);
  const hasVoted = userVoteIndex !== -1 || !!userOtherVote;
  const hasAnyOptionVotes = poll.options.some((_, idx) => getOptionVotes(idx).length > 0);

  const handleVote = async (optionIndex) => {
    if (submittingVote || readOnly) return;

    // Single-select: clicking the option you're already on is a no-op.
    // Multi-select: clicking toggles (so you can deselect) — never a no-op.
    if (!allowMultiple && userVoteIndex === optionIndex) return;

    setSubmittingVote(true);

    // Optimistic update — reflect the click immediately.
    const updatedPoll = JSON.parse(JSON.stringify(poll));
    const opt = updatedPoll.options[optionIndex];

    if (allowMultiple) {
      // Toggle ONLY this option; leave the others (and any Other response) as-is.
      if (typeof opt === 'object') {
        if (!opt.votes) opt.votes = [];
        const i = opt.votes.indexOf(currentUserId);
        if (i !== -1) opt.votes.splice(i, 1);
        else opt.votes.push(currentUserId);
      } else {
        updatedPoll.options[optionIndex] = { text: opt, votes: [currentUserId] };
      }
    } else {
      // Single-select: remove any existing vote, then add to the chosen option.
      updatedPoll.options.forEach((o) => {
        if (typeof o === 'object' && o.votes) {
          const idx = o.votes.indexOf(currentUserId);
          if (idx !== -1) o.votes.splice(idx, 1);
        }
      });
      if (typeof opt === 'object') {
        if (!opt.votes) opt.votes = [];
        opt.votes.push(currentUserId);
      } else {
        updatedPoll.options[optionIndex] = { text: opt, votes: [currentUserId] };
      }
    }
    setLocalPoll(updatedPoll);
    setForceUpdate(f => f + 1);

    try {
      // Injected persistence. Feed returns the authoritative poll; DM returns
      // nothing and relies on the onSnapshot-driven `pollProp` update instead.
      const result = await onVote?.(optionIndex);
      if (result) {
        setLocalPoll(result);
        setForceUpdate(f => f + 1);
      }
    } catch (err) {
      console.error('Error voting:', err);
      // Revert on failure
      setLocalPoll(JSON.parse(JSON.stringify(pollProp)));
      setForceUpdate(f => f + 1);
    } finally {
      setSubmittingVote(false);
    }
  };

  const handleOtherVote = async () => {
    if (submittingVote || readOnly || !otherResponse.trim()) return;
    setSubmittingVote(true);
    const text = otherResponse.trim();
    try {
      const result = await onVoteOther?.(text);
      setOtherResponse('');
      if (result) {
        setLocalPoll(result);
      }
    } catch (err) {
      console.error('Error voting:', err);
    } finally {
      setSubmittingVote(false);
    }
  };
  
  const votersContent = (
      <PopoverContent className="w-64 p-0" align="start">
        <div className="max-h-72 overflow-y-auto">
          {poll.options
            .map((option, idx) => ({ text: typeof option === 'object' ? option.text : option, voters: getOptionVotes(idx), idx }))
            .filter(o => o.voters.length > 0)
            .sort((a, b) => b.voters.length - a.voters.length)
            .map(({ text, voters, idx }, mapIdx) => (
              <div key={idx}>
                <div className={`px-3 py-2 bg-slate-50 dark:bg-[#1e1f22] border-b border-slate-100 dark:border-white/10 ${mapIdx === 0 ? 'rounded-t-xl' : ''}`}>
                  <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">{text} ({voters.length})</span>
                </div>
                {voters.map(uid => (
                  <div key={uid} className="flex items-center gap-2 px-3 py-1.5">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${uid === currentUserId ? 'bg-blue-400' : 'bg-slate-300'}`} />
                    <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{voterNames[uid] || 'User'}{uid === currentUserId ? ' (you)' : ''}</span>
                  </div>
                ))}
              </div>
            ))}
          {poll.other_responses?.length > 0 && (
            <div>
              <div className={`px-3 py-2 bg-slate-50 dark:bg-[#1e1f22] border-b border-slate-100 dark:border-white/10 ${!hasAnyOptionVotes ? 'rounded-t-xl' : ''}`}>
                <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Other ({poll.other_responses.length})</span>
              </div>
              {poll.other_responses.map((r, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5">
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${r.user_id === currentUserId ? 'bg-blue-400' : 'bg-slate-300'}`} />
                  <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{voterNames[r.user_id] || 'User'}{r.user_id === currentUserId ? ' (you)' : ''}: {r.response}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </PopoverContent>
  );

  if (isStudio) {
    // Studio (104 §6.2): 38px hairline rows, the vote share as a quiet fill,
    // the leader's fill one step darker, a check on the option(s) you chose,
    // and one meta line "n votes · Who voted". Same handlers and testids.
    const counts = poll.options.map((_, idx) => getOptionVotes(idx).length);
    const lead = counts.reduce((best, n, idx) => (n > counts[best] ? idx : best), 0);
    return (
      <div className="st-poll" data-testid="poll-display">
        {!hideQuestion && <div className="st-pollq">{poll.question}</div>}
        {allowMultiple && (
          <p className="st-pollhint" data-testid="poll-multi-hint">Select all that apply</p>
        )}
        {poll.options.map((option, index) => {
          const n = counts[index];
          const pct = totalVotes > 0 ? Math.round((n / totalVotes) * 100) : 0;
          const isSelected = allowMultiple ? getOptionVotes(index).includes(currentUserId) : userVoteIndex === index;
          return (
            <button
              key={`${index}-${forceUpdate}`}
              type="button"
              onClick={() => handleVote(index)}
              disabled={submittingVote}
              className={`st-popt${isSelected ? ' st-mine' : ''}${hasVoted && n > 0 && index === lead ? ' st-lead' : ''}`}
              data-testid={`poll-option-vote-${index}`}
            >
              <i style={{ width: hasVoted ? `${pct}%` : 0 }} />
              {isSelected && <Check />}
              <span>{typeof option === 'object' ? option.text : option}</span>
              {hasVoted && <span className="st-pct">{n} · {pct}%</span>}
            </button>
          );
        })}
        {poll.allow_other && (
          <div className="st-other">
            <input
              placeholder={userOtherVote ? 'Change your answer…' : 'Other (your answer)…'}
              value={otherResponse}
              onChange={(e) => setOtherResponse(e.target.value)}
              disabled={submittingVote}
              data-testid="poll-other-input"
            />
            <button type="button" className="st-btn st-sec st-sm" onClick={handleOtherVote}
                    disabled={submittingVote || !otherResponse.trim()}>
              {userOtherVote ? 'Update' : 'Submit'}
            </button>
          </div>
        )}
        {poll.allow_other && poll.other_responses?.length > 0 && (
          <div className="st-otherlist">
            {poll.other_responses.map((r, i) => (
              <div key={i} className={r.user_id === currentUserId ? 'st-mine' : undefined}>
                {r.response}{r.user_id === currentUserId && ' (your vote)'}
              </div>
            ))}
          </div>
        )}
        <div className="st-pm">
          <span className="st-num">{totalVotes} {allowMultiple ? 'selection' : 'vote'}{totalVotes !== 1 ? 's' : ''}</span>
          {closesLabel && (<><span>·</span><span>Closes {closesLabel}</span></>)}
          {hasVoted && totalVotes > 0 && (
            <>
              <span>·</span>
              <Popover>
                <PopoverTrigger asChild>
                  <button type="button" data-testid="poll-voters-btn">Who voted</button>
                </PopoverTrigger>
                {votersContent}
              </Popover>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3 p-5 bg-gradient-to-br from-slate-50 dark:from-[#1e1f22] to-blue-50/30 rounded-2xl border border-slate-200/80 shadow-sm" data-testid="poll-display">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
          <BarChart3 className="w-4 h-4 text-white" />
        </div>
        <span className="font-semibold text-slate-800 dark:text-slate-100">{poll.question}</span>
      </div>
      {allowMultiple && (
        <p className="-mt-2 mb-3 ml-[42px] text-xs font-medium text-slate-400 dark:text-slate-500" data-testid="poll-multi-hint">
          Select all that apply
        </p>
      )}
      
      <div className="space-y-2.5">
        {poll.options.map((option, index) => {
          const optionVotes = getOptionVotes(index).length;
          const percentage = totalVotes > 0 ? Math.round((optionVotes / totalVotes) * 100) : 0;
          const isSelected = allowMultiple ? getOptionVotes(index).includes(currentUserId) : userVoteIndex === index;
          
          return (
            <button
              key={`${index}-${forceUpdate}`}
              onClick={() => handleVote(index)}
              disabled={submittingVote}
              className={`w-full text-left relative overflow-hidden rounded-xl border-2 transition-all ${
                isSelected 
                  ? 'border-blue-400 bg-card shadow-md shadow-blue-500/10' 
                  : 'border-slate-200 dark:border-white/10 bg-card hover:border-blue-400 hover:shadow-md cursor-pointer'
              }`}
              data-testid={`poll-option-vote-${index}`}
            >
              {/* Progress bar background */}
              {hasVoted && (
                <div 
                  className={`absolute inset-y-0 left-0 transition-all duration-700 ease-out ${isSelected ? 'bg-gradient-to-r from-blue-100 to-blue-50 dark:from-blue-500/25 dark:to-blue-500/10' : 'bg-slate-100/70 dark:bg-white/5'}`}
                  style={{ width: `${percentage}%` }}
                />
              )}
              <div className="relative px-4 py-3.5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-5 h-5 ${allowMultiple ? 'rounded-md' : 'rounded-full'} border-2 flex items-center justify-center transition-all ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-500' 
                      : hasVoted 
                        ? 'border-slate-300 dark:border-white/15 bg-card' 
                        : 'border-slate-300 dark:border-white/15 bg-card group-hover:border-blue-400'
                  }`}>
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span className={`text-sm ${isSelected ? 'font-semibold text-blue-700 dark:text-blue-300' : 'text-slate-700 dark:text-slate-200'}`}>
                    {typeof option === 'object' ? option.text : option}
                  </span>
                </div>
                {hasVoted && (
                  <span className={`text-sm font-bold ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400 dark:text-slate-500'}`}>{percentage}%</span>
                )}
              </div>
            </button>
          );
        })}
        
        {/* Other option */}
        {poll.allow_other && (
          <div className="mt-3">
            {/* Always show input to allow changing vote */}
            <div className="flex gap-2">
              <Input
                placeholder={userOtherVote ? "Change your answer..." : "Other (your answer)..."}
                value={otherResponse}
                onChange={(e) => setOtherResponse(e.target.value)}
                className="flex-1 text-sm"
                disabled={submittingVote}
                data-testid="poll-other-input"
              />
              <Button
                size="sm"
                onClick={handleOtherVote}
                disabled={submittingVote || !otherResponse.trim()}
                className="bg-blue-500 hover:bg-blue-600"
              >
                {userOtherVote ? 'Update' : 'Submit'}
              </Button>
            </div>
            
            {/* Show other responses */}
            {poll.other_responses?.length > 0 && (
              <div className="mt-2 pt-2 border-t border-slate-200 dark:border-white/10">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Other responses:</p>
                <div className="space-y-1">
                  {poll.other_responses.map((r, i) => (
                    <div key={i} className={`text-sm px-3 py-2 rounded-lg ${
                      r.user_id === currentUserId ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-medium' : 'bg-slate-100 dark:bg-[#3a3d41] text-slate-600 dark:text-slate-300'
                    }`}>
                      {r.response}
                      {r.user_id === currentUserId && <span className="ml-2 text-xs">(your vote)</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="flex items-center gap-2 mt-3">
        {hasVoted && totalVotes > 0 ? (
          <Popover>
            <PopoverTrigger asChild>
              <button className="text-xs text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer" data-testid="poll-voters-btn">
                {totalVotes} {allowMultiple ? 'selection' : 'vote'}{totalVotes !== 1 ? 's' : ''} • {allowMultiple ? 'Tap options to add or remove' : 'You voted (tap to change)'}
              </button>
            </PopoverTrigger>
            {votersContent}
          </Popover>
        ) : (
          <p className="text-xs text-slate-400 dark:text-slate-500">
            {totalVotes} {allowMultiple ? 'selection' : 'vote'}{totalVotes !== 1 ? 's' : ''}
          </p>
        )}
      </div>
    </div>
  );
}


/**
 * PollDisplay — Team Comms feed wrapper (MongoDB-backed).
 * Thin adapter around the shared <PollView>: supplies the Mongo vote endpoints
 * and server-resolved voter names. Behavior is identical to before the extract.
 *
 * @param {Object} post     - feed post containing `poll` + `poll_voter_names`
 * @param {Object} user     - current user ({ id })
 * @param {Function} onUpdate - (unused) retained for caller compatibility
 */
export function PollDisplay({ post, user, onUpdate }) { // eslint-disable-line no-unused-vars
  if (!post?.poll) return null;

  const onVote = async (optionIndex) => {
    const response = await fetch(`${API_URL}/api/comms/v3/posts/${post.id}/poll/vote?user_id=${encodeURIComponent(user.id)}&option_index=${optionIndex}`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Vote failed');
    const data = await response.json();
    return data.poll;
  };

  const onVoteOther = async (text) => {
    const response = await fetch(`${API_URL}/api/comms/v3/posts/${post.id}/poll/vote-other?user_id=${encodeURIComponent(user.id)}&response=${encodeURIComponent(text)}`, {
      method: 'POST'
    });
    if (!response.ok) throw new Error('Vote failed');
    const data = await response.json();
    return data.poll;
  };

  // Studio: when the post body *is* the question, do not print it twice.
  const bodyText = (post.content || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const hideQuestion = !!post.poll.question && bodyText === String(post.poll.question).trim();

  return (
    <PollView
      poll={post.poll}
      currentUserId={user.id}
      voterNames={post.poll_voter_names || {}}
      onVote={onVote}
      onVoteOther={onVoteOther}
      hideQuestion={hideQuestion}
    />
  );
}
